import api from '.'
import { endpoint } from './api'

const product = endpoint('product', ['all', 'one'])

const cache = {}

/**
 * A map of all the products used with PNUM's
 */
const productPnum = {
    quicklinkViewFactor: '200500500',
    quicklinkDownloadFactor: '200500503',
    storageVelocity: '200200010',
    storageDensity: '200200020',
    awsGlacier: '200200050',
    awsDeepGlacier: '200200060',
    masterDeliveryOneMinute: '200900110',
    masterDeliveryFiveMinutes: '200900120',
    masterDeliveryThirtyMinutes: '200900130',
    masterDeliverySixtyMinutes: '200900140',
    masterDeliveryNinetyMinutes: '200900150',
    masterDeliveryOneTwentyMinutes: '200900160',
    migrationHour: '200500182',
    transactionLowFactor: '200500110',
    transactionStandardFactor: '200500111',
    transactionHighFactor: '200500112',
    transactionCustom: '200500113',
    s3Downloads: '200500200'
}

/**
 * Filter out products not defined in productPnum
 * @see productPnum for a list of used products
 * @param {object} products All products
 */
const filterProducts = (products) => {
    const pnums = Object.values(productPnum)
    //console.log(pnums)
    //console.log(products.filter((p) => p['pnum'] === '200500503'))
    return products
        .filter((p) => pnums.includes(p['pnum']))
        .reduce(
            (acc, { pnum, name, price_eur, long_desc, short_desc }) => ({
                ...acc,
                [pnum]: { name, price_eur, long_desc, short_desc }
            }),
            {}
        )
}

const getProducts = async () => {
    let products
    if (cache.all) {
        products = cache.all
    } else {
        products = filterProducts(await api.product.all())
        cache.all = products
    }
    return products
}

/**
 * Use to get the descriptions of all relevant products
 */
const getDescriptions = async () => {
    const products = await getProducts()

    const description = (pnum) => {
        return products[pnum].long_desc
    }

    return {
        mboxLow: description(productPnum.transactionLowFactor),
        mboxStandard: description(productPnum.transactionStandardFactor),
        mboxHigh: description(productPnum.transactionHighFactor),
        mboxCustom: description(productPnum.transactionCustom),
        s3Downloads: description(productPnum.s3Downloads),
        quicklinkViews: description(productPnum.quicklinkViewFactor),
        quicklinkDownloads: description(productPnum.quicklinkDownloadFactor),
        highVelocity: description(productPnum.storageVelocity),
        highDensity: description(productPnum.storageDensity),
        awsGlacier: description(productPnum.awsGlacier),
        awsDeepGlacier: description(productPnum.awsDeepGlacier),
        migration: description(productPnum.migrationHour)
    }
}

/**
 * Use to generate a price list from a list of our products
 * @returns A list of products with prices/factors
 */
const getPrices = async () => {
    const products = await getProducts()

    const currency = await api.config.one('currency_config')

    const price = (pnum) => {
        try {
            return products[pnum]['price_eur']
        } catch (error) {
            throw Error(`${error}\nProduct with pnum: ${pnum}`)
        }
    }

    const factor = (pnum) => {
        /**
         * Find the factor from the description of the product
         * @param {'long_desc' || 'short_desc'} desc
         */
        const findFactor = (desc) =>
            products[pnum][desc].match(/Factor \d+,+\d+/g)

        try {
            let num = findFactor('long_desc')
            if (!num) num = findFactor('short_desc')
            num = Number(num[0].split(' ')[1].replace(',', '.'))
            if (isNaN(num)) throw Error(`"${num}" is not a number`)
            else return num
        } catch (error) {
            throw error
        }
    }

    return {
        quicklink: {
            factor: {
                download: factor(productPnum.quicklinkDownloadFactor),
                view: factor(productPnum.quicklinkViewFactor)
            },
            price: {
                download: {
                    gb: 0.2 // Try and fail number
                }
            }
        },
        masterDelivery: {
            oneMinute: price(productPnum.masterDeliveryOneMinute),
            fiveMinutes: price(productPnum.masterDeliveryFiveMinutes),
            thirtyMinutes: price(productPnum.masterDeliveryThirtyMinutes),
            sixtyMinutes: price(productPnum.masterDeliverySixtyMinutes),
            ninetyMinutes: price(productPnum.masterDeliveryNinetyMinutes),
            oneTwentyMinutes: price(productPnum.masterDeliveryOneTwentyMinutes)
        },
        mbox: {
            factor: {
                low: factor(productPnum.transactionLowFactor),
                standard: factor(productPnum.transactionStandardFactor),
                high: factor(productPnum.transactionHighFactor),
                custom: 2.5
            },
            price: {
                min: 1.88
            }
        },
        migration: {
            price: {
                hour: price(productPnum.migrationHour)
            }
        },
        currency: {
            factor: {
                eur: 1,
                usd: currency.base.eur.usd.rate
            }
        },
        storage: {
            awsDeepGlacier: {
                tbPrice: price(productPnum.awsDeepGlacier)
            },
            awsGlacier: {
                tbPrice: price(productPnum.awsGlacier)
            },
            density: {
                tbPrice: price(productPnum.storageDensity)
            },
            velocity: {
                tbPrice: price(productPnum.storageVelocity)
            }
        }
    }
}

export default {
    ...product,
    getPrices,
    getDescriptions
}
